import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { Home, Photo, Settings, Radio } from '@material-ui/icons';
import { fade } from '@material-ui/core/styles/colorManipulator';

const DESKTOP_BREAKPOINT = 900;

const useStyles = makeStyles(theme => ({
    tabsRoot: props => ({
        minHeight: 48,
        width: '100%',
        maxWidth: '100%',
        padding: theme.spacing(0.5),
        borderRadius: 999,
        backgroundColor: fade(props.contrastText, 0.12),
        overflowX: 'auto',
        overflowY: 'hidden',
        '@media (min-width:900px)': {
            overflow: 'hidden',
        },
    }),
    scroller: {
        width: '100%',
    },
    flexContainer: {
        [theme.breakpoints.down('sm')]: {
            justifyContent: 'center',
        },
    },
    indicator: {
        display: 'none',
    },
    tabRoot: props => ({
        minHeight: 40,
        minWidth: 110,
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '0.01em',
        borderRadius: 999,
        padding: theme.spacing(0.75, 2),
        margin: theme.spacing(0, 0.5),
        color: fade(props.contrastText, 0.72),
        transition: theme.transitions.create(['background-color', 'box-shadow', 'color'], {
            duration: theme.transitions.duration.shortest,
        }),
        '& .MuiTab-wrapper': {
            flexDirection: 'row',
            gap: theme.spacing(0.75),
        },
        [theme.breakpoints.down('sm')]: {
            minWidth: 86,
            padding: theme.spacing(0.6, 1.25),
            margin: theme.spacing(0, 0.25),
            '& .MuiTab-wrapper': {
                gap: theme.spacing(0.5),
            },
        },
    }),
    tabSelected: props => ({
        color: props.contrastText,
        backgroundColor: fade(props.contrastText, 0.2),
        boxShadow: `0 8px 16px ${fade(props.contrastText, 0.25)}`,
    }),
}));

const tabConfig = [
    { value: 0, label: 'Home', to: '/home', icon: <Home /> },
    { value: 1, label: 'Visual', to: '/visual', icon: <Photo /> },
    { value: 2, label: 'Live', to: '/live', icon: <Radio /> },
    { value: 3, label: 'Settings', to: '/profile', icon: <Settings /> },
];

const getTabValue = pathname => {
    if (pathname.includes('home')) {
        return 0;
    }
    if (pathname.includes('visual')) {
        return 1;
    }
    if (pathname.includes('live')) {
        return 2;
    }
    if (pathname.includes('profile')) {
        return 3;
    }
    return false;
};

const ScrollableTabsButtonForce = ({ user, selectedColor, hideProfileTabOnMobile = false }) => {
    const theme = useTheme();
    const location = useLocation();
    const isMobile = useMediaQuery(`(max-width:${DESKTOP_BREAKPOINT - 1}px)`);
    const baseColor = selectedColor || user.color || theme.palette.primary.main;
    const contrastText = theme.palette.getContrastText(baseColor);
    const classes = useStyles({ contrastText });

    const tabsToRender = hideProfileTabOnMobile ? tabConfig.filter(tab => tab.value !== 3) : tabConfig;

    const [value, setValue] = React.useState(getTabValue(location.pathname));

    React.useEffect(() => {
        const nextValue = getTabValue(location.pathname);
        const hasMatchingTab = tabsToRender.some(tab => tab.value === nextValue);
        setValue(hasMatchingTab ? nextValue : false);
    }, [location.pathname, hideProfileTabOnMobile]);

    const handleChange = (event, nextValue) => {
        setValue(nextValue);
    };

    return (
        <Tabs
            value={value}
            onChange={handleChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : 'off'}
            allowScrollButtonsMobile
            centered={!isMobile}
            aria-label="Main navigation"
            classes={{ root: classes.tabsRoot, scroller: classes.scroller, flexContainer: classes.flexContainer, indicator: classes.indicator }}
        >
            {tabsToRender.map(tab => (
                <Tab
                    key={tab.value}
                    value={tab.value}
                    label={tab.label}
                    component={Link}
                    to={tab.to}
                    icon={tab.icon}
                    classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
                />
            ))}
        </Tabs>
    );
};

const mapStateToProps = state => ({
    user: state.user,
});

export default connect(mapStateToProps)(ScrollableTabsButtonForce);
